import { AxiosResponse } from 'axios';
import { TFunction } from 'i18next';
import { SignJWT } from 'jose';
import isString from 'lodash.isstring';
import moment from 'moment';
import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import cssForms from '../../prime/assets/scss/Forms.scss';
import { config } from '../../prime/common';
import { ConfigBase } from '../types/config';

export function checkHttpStatus(response: AxiosResponse) {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }

    const error: any = new Error(response.statusText);
    error.response = response;
    throw error;
}

export function parseJSON(response: any): object {
    return response.json();
}

let originalConsoleError: any = null;

// Patch console.error to ignore all errors that match the pattern given.
export function ignoreConsoleErrors(pattern: string) {
    originalConsoleError = window.console.error;

    window.console.error = (...args: any[]) => {
        let message: string | null = null;
        if (isString(args[0])) {
            message = args[0];
        }

        // If it matches, continue to console.error as normal.
        if (!(message && message.match(pattern))) {
            return originalConsoleError.apply(console, args);
        }
        return null;
    };
}

export function restoreConsoleError() {
    window.console.error = originalConsoleError;
}

export function iframeScrollTo(elemId: string) {
    const elem = document.getElementById(elemId);
    if (config.UseIFrameLogic && elem) {
        const elemParent = parent.document.getElementById(config.IframeId);
        if (elemParent) {
            parent.document.documentElement.scrollTo({
                top: elem.offsetTop + elemParent.offsetTop,
                behavior: 'smooth',
            });
        } else {
            document.documentElement.scrollTo({
                top: elem.offsetTop,
                behavior: 'smooth',
            });
        }
    }
}

export function iframeScrollToTop() {
    if (config.UseIFrameLogic) {
        const w: any = window;
        window.scrollTo(0, 0);
        if (w.parentIFrame) {
            w.parentIFrame.scrollTo(0, 0);
        }
    }
}

export function iframelinkToTop(event: any, url: any) {
    event.preventDefault();
    iframeScrollToTop();
    window.location = url;
}

export function iframeScrollToCenter() {
    if (config.UseIFrameLogic) {
        const elemRoot = document.getElementById('root');
        if (elemRoot) {
            window.scrollTo({
                top: (elemRoot.scrollHeight / 2),
                behavior: 'instant',
            });
        }

        // const elemParent = window.parent.document.getElementById(config.IframeId);
        // const elemRoot = document.getElementById('root');
        // if (elemParent && elemRoot) {
        //     window.parent.document.documentElement.scrollTo({
        //         top: (elemRoot.scrollHeight / 2) + elemParent.offsetTop - (parent.innerHeight / 2),
        //         behavior: 'instant',
        //     });
        // }
    }
}

export function normalizePlate(plate: string | null | undefined) {
    return plate && plate.replace(/-/g, '').toUpperCase();
}

interface IResizeImageOptions {
    maxSize: number;
    file: File;
}
export const resizeImage = (settings: IResizeImageOptions) => {
    const file = settings.file;
    const maxSize = settings.maxSize;
    const reader = new FileReader();
    const image = new Image();
    const canvas = document.createElement('canvas');
    const dataURItoBlob = (dataURI: string) => {
        const bytes = dataURI.split(',')[0].indexOf('base64') >= 0 ?
            atob(dataURI.split(',')[1]) :
            unescape(dataURI.split(',')[1]);
        const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const max = bytes.length;
        const ia = new Uint8Array(max);
        for (let i = 0; i < max; i++) { ia[i] = bytes.charCodeAt(i); }
        return new Blob([ia], { type: mime });
    };
    const resize = () => {
        let width = image.width;
        let height = image.height;
        let sWidth = 0;
        let sHeight = 0;
        if (width > height) {
            sWidth = (width - height) / 2;
            width = height;
        } else if (height > width) {
            sHeight = (height - width) / 2;
            height = width;
        }

        let imageData: string = '';
        canvas.width = maxSize;
        canvas.height = maxSize;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(image, sWidth, sHeight, width, height, 0, 0, maxSize, maxSize);

            const data = ctx.getImageData(0, 0, maxSize, maxSize);
            const compositeOperation = ctx.globalCompositeOperation;
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, maxSize, maxSize);
            imageData = canvas.toDataURL('image/jpeg');
            ctx.clearRect(0, 0, maxSize, maxSize);
            ctx.putImageData(data, 0, 0);
            ctx.globalCompositeOperation = compositeOperation;
        }
        return dataURItoBlob(imageData);

        // const dataUrl = canvas.toDataURL('image/jpeg');
        // return dataURItoBlob(dataUrl);
    };

    return new Promise((ok, no) => {
        if (!file.type.match(/image.*/)) {
            no(new Error('Not an image'));
            return;
        }

        reader.onload = (readerEvent: any) => {
            image.onload = () => ok(resize());
            image.src = readerEvent.target.result;
        };
        reader.readAsDataURL(file);
    });
};

export async function generateJwtState(redirect: string) {
    const validation = Math.floor((Math.random() * 100000000) + 10000000);
    const secret = new TextEncoder().encode(config.JwtSecret);
    return await new SignJWT({ validation, redirect })
        .setProtectedHeader({ alg: 'HS256' })
        .setIssuedAt()
        .setExpirationTime('2h') // Optional, but recommended
        .sign(secret);
}

export async function generateJwtStateWithParams(redirect: string, params: any) {
    const validation = Math.floor((Math.random() * 100000000) + 10000000);
    const secret = new TextEncoder().encode(config.JwtSecret);
    return await new SignJWT({ validation, redirect, params })
        .setProtectedHeader({ alg: 'HS256' })
        .setIssuedAt()
        .setExpirationTime('2h') // Optional, but recommended
        .sign(secret);
}

export function confirmPopup(message: string, onConfirm: () => void, t: TFunction) {
    confirmAlert({
        customUI: ({ onClose }) => {
            return (
                <div className={cssForms.confirmAlert}>
                    <h1>{t('confirmTitle')}</h1>
                    <p>{message}</p>

                    <div className={[cssForms.buttons, 'col-12'].join(' ')}>
                        <div className={[cssForms.bmwButton, cssForms.gray].join(' ')} onClick={onClose}>
                            <b>{t('bool.no')}</b>
                        </div>
                        <div
                            className={[cssForms.bmwButton, cssForms.blue, cssForms.submit].join(' ')}
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                        >
                            {t('bool.yes')}
                        </div>
                    </div>
                </div>
            );
        },
    });
    iframeScrollToCenter();
}

export const sortDateAsc = (a: Date, b: Date, dateFormat: string) => {
    const aF = moment(a, dateFormat);
    const bF = moment(b, dateFormat);
    if (aF.isBefore(bF)) {
        return -1;
    } else if (bF.isBefore(aF)) {
        return 1;
    }
    return 0;
};

export const sortDateTimeAsc = (a: Date, b: Date, dateFormat: string) => {
    return sortDateAsc(a, b, dateFormat);
};

export const getI18NextErrorMessageToken = (configB: ConfigBase, errorCode: string): string => {
    const errorCodes = configB.Constants.ErrorCodes;
    switch (errorCode) {
        case errorCodes.AuthenticationFailed:
            return 'signin:errorMessages:authenticationFailed';
        case errorCodes.InvalidUsernameOrPassword:
            return 'signin:errorMessages:invalidUsernameOrPassword';
        case errorCodes.SessionExpired:
            return 'common:errorMessages:sessionExpired';
        case errorCodes.UnknownError:
        default:
            return 'common:errorMessages:unknownError';
    }
};

export function saveData(data: Blob, fileName: string) {
    const dataStr = String(data);
    const a = document.createElement('a');
    document.body.appendChild(a);
    const arrBuffer = base64ToArrayBuffer(dataStr);
    const blob = new Blob([arrBuffer], { type: 'application/pdf' });

    const fileURL = URL.createObjectURL(blob);
    // Open the URL on new Window
    // window.open(fileURL);

    a.href = fileURL;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(fileURL);
}

function base64ToArrayBuffer(data: string) {
    const binaryString = window.atob(data);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
        const ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
    }
    return bytes;
}

export const groupBy = (xs: any, key: string) => {
    return xs.reduce((rv: any, x: any) => {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
};

// 2023-10: Removed tracy as per BMW's request
// export function CallTracyPage() {
//    try {
//        // @ts-ignore
//        if (window && window.location && window.location.href && tracy) {
//            const tracyUrl = window.location.href.replace(config.ApiServerHostName, '');
//            console.log(`Tracy -> ${tracyUrl}}`); // tslint:disable-line
//            // @ts-ignore
//            tracy.page(tracyUrl, '');
//            console.log(`Tracy completed`); // tslint:disable-line
//        }
//    } catch (ex) {
//        console.log(`CallTracyPage exception: ${ex}`); // tslint:disable-line
//    }
// }
